<template>
	<AppBottomSheet :active="active" :force-width="bottomSheetWidth" @toggling-popup="open => $emit('toggling-popup', open)">
		<div v-if="active">
			<ModalHeader
				:title="getString('self_invoice_title')"
				:info-button="getString('self_invoice_explain')"
				:button-text="getString('self_invoice_add_rule')"
				:button-disabled="loading"
				@click-button="addMerchant" />

			<AppLoader v-if="loading" :text="getString('loading')" />
			<template v-else>
				<AppList v-if="rules.length" :items="rulesItems">
					<template #rule="{item}">
						<div class="rule-row">
							<div class="rule-texts">
								<p class="rule-title">{{ item.rule.pattern }} <span class="rule-match">· {{ matchTypeName(item.rule.match_type) }}</span></p>
								<p class="rule-subtitle">{{ item.rule.supplier_name }} · {{ item.rule.supplier_country_code }} · {{ item.rule.supplier_vat }} · {{ getString('vat') }} {{ Number(item.rule.vat_rate) }}%</p>
							</div>
							<div class="rule-actions">
								<AppButtonIcon icon-name="icon-edit-white" prevent-change :title="getString('edit')" @click-icon="openForm(item.rule)" />
								<AppButtonIcon icon-name="icon-trash-white" prevent-change :title="getString('delete')" :loading="deletingRuleId === item.rule.rule_id" @click-icon="askDelete(item.rule)" />
							</div>
						</div>
					</template>
				</AppList>
				<p v-else class="self-invoice-empty">{{ getString('self_invoice_no_rules') }}</p>
			</template>
		</div>
	</AppBottomSheet>

	<!-- Rendered after the list so it stacks over it -->
	<CardsSelfInvoiceMerchantsPicker
		:active="pickerOpen"
		@toggling-popup="closePicker"
		@picked-merchant="openFormFromMerchant"
		@other-merchant="openForm(null)" />

	<AppBottomSheet :active="formOpen" :force-width="bottomSheetWidth" @toggling-popup="closeForm">
		<div v-if="formOpen">
			<ModalHeader
				:title="getString(editingRuleId ? 'self_invoice_edit_rule' : 'self_invoice_add_rule')"
				:button-text="getString('save')"
				:button-disabled="!isFormValid"
				:button-loading="saving"
				@click-button="saveRule" />
			<div class="self-invoice-form" :class="{wide: isWideForm}">
				<p class="self-invoice-section-title">{{ getString('self_invoice_merchant_match') }}</p>
				<div class="form-grid merchant-grid">
					<AppTextInput :label="getString('self_invoice_pattern')" :value="form.pattern" :placeholder="getString('merchant_name')" @changing-text="v => setPattern(v)" />
					<AppSelect :label="getString('self_invoice_match_type')" :values="matchTypes.map(t => matchTypeName(t))" :selected-index="matchTypes.indexOf(form.match_type)" @selected="index => setMatchType(matchTypes[index])" />
				</div>
				<div v-if="!editingRuleId && preview.count > 0" class="backfill-row">
					<p>{{ getString('self_invoice_backfill', [preview.count, currencyStore.readableCurrencyValue(preview.total_amount, true, true, false)]) }}</p>
					<AppSwitch :checked="backfill" @switched="v => backfill = v" />
				</div>

				<p class="self-invoice-section-title">{{ getString('self_invoice_supplier') }}</p>
				<div class="form-grid">
					<AppTextInput :label="getString('self_invoice_supplier_name')" :value="form.supplier_name" @changing-text="v => form.supplier_name = v" />
					<div>
						<AppTextInput :label="getString('vat')" :value="form.supplier_vat" @changing-text="v => form.supplier_vat = v" />
						<p v-if="form.supplier_country_code && !isEuCountry" class="form-hint">{{ getString('self_invoice_extra_eu_vat_hint') }}</p>
					</div>
					<AppTextInput
						class="country-input"
						readonly
						:label="getString('country')"
						:value="form.supplier_country_code ? countryName(form.supplier_country_code) : ''"
						:placeholder="getString('select_country')"
						@click-input="countriesOpen = true" />
					<!-- Keyed on the country so the autocomplete restriction follows it -->
					<AppGoogleMapsInput
						v-if="form.supplier_country_code"
						:key="form.supplier_country_code"
						:label="getString('legal_address')"
						:placeholder="getString('address')"
						:value="formattedAddress"
						:country-restriction="form.supplier_country_code"
						no-map
						@changed-address="setAddress" />
				</div>

				<p class="self-invoice-section-title">{{ getString('self_invoice_invoice') }}</p>
				<div class="form-grid invoice-grid">
					<AppNumberInput :label="getString('vat_rate')" :value="form.vat_rate" :min="1" :max="100" symbol="%" right-side-symbol @changed-value="v => form.vat_rate = v" />
					<AppTextInput :label="getString('description')" :value="form.description" :placeholder="defaultDescription" @changing-text="v => form.description = v" />
				</div>
			</div>
		</div>
	</AppBottomSheet>

	<CompanyCountriesBottomsheet :active="countriesOpen" @selected-country="selectCountry" @toggling-popup="v => countriesOpen = v" />

	<AppPopup
		ref="delete-popup"
		:title="getString('delete')"
		:body-text="getString('self_invoice_confirm_delete')"
		:buttons="deletePopupButtons" />
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppList from "@/shared/components/AppList.vue";
import CardsSelfInvoiceMerchantsPicker from "@/console-new/components/config/administration/cards/CardsSelfInvoiceMerchantsPicker.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import CompanyCountriesBottomsheet from "@/console-new/components/shared/CompanyCountriesBottomsheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import euCountriesFile from "@/shared/assets/json/euCountrycodes.json";
import nonEuCountriesFile from "@/shared/assets/json/nonEuCountrycodes.json";
import API from "@/shared/helpers/API";
import {useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";

// Foreign suppliers without a VAT number, as SDI expects them
const EXTRA_EU_VAT = "99999999999";
const MATCH_TYPES = ["exact", "contains", "starts_with"];
const INVALID_RULE_ERRORS = ["invalid_pattern", "missing_supplier_data", "invalid_supplier_country", "invalid_supplier_vat", "invalid_vat_rate"];

function descriptionFor(name) {
	return name.trim() ? "Servizi " + name.trim() : "";
}

function emptyForm() {
	return {
		pattern: "",
		match_type: "exact",
		supplier_name: "",
		supplier_vat: "",
		supplier_address: "",
		supplier_civic: "",
		supplier_zip: "",
		supplier_city: "",
		supplier_country_code: "",
		vat_rate: 22,
		description: "",
	};
}

export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		return {userStore, currencyStore, getString: languageStore.getString, showNotification: notificationStore.showNotification};
	},
	name: "CardsSelfInvoicesBottomSheet",
	components: {AppBottomSheet, AppGoogleMapsInput, AppButtonIcon, AppList, AppLoader, AppNumberInput, AppPopup, AppSelect, AppSwitch, AppTextInput, ModalHeader, CompanyCountriesBottomsheet, CardsSelfInvoiceMerchantsPicker},
	mixins: [WindowWidth],
	emits: ["toggling-popup", "saved"],
	props: {
		active: {type: Boolean, default: false},
	},
	data() {
		return {
			pickerOpen: false,
			formOpen: false,
			loading: false,
			rules: [],
			matchTypes: MATCH_TYPES,
			form: emptyForm(),
			editingRuleId: null,
			saving: false,
			backfill: false,
			preview: {count: 0, total_amount: 0},
			previewTimeout: null,
			countriesOpen: false,
			ruleToDelete: null,
			deletingRuleId: null,
		};
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "760px";
			}
		},
		isWideForm() {
			return this.windowWidth >= 768;
		},
		formattedAddress() {
			const f = this.form;
			if(!f.supplier_address) {
				return "";
			}
			const street = [f.supplier_address, f.supplier_civic].filter(Boolean).join(" ");
			const city = [f.supplier_zip, f.supplier_city].filter(Boolean).join(" ");
			return [street, city].filter(Boolean).join(", ");
		},
		rulesItems() {
			return this.rules.map(rule => ({title: rule.pattern, customSlot: "rule", uniqueId: rule.rule_id, rule}));
		},
		isEuCountry() {
			return Object.prototype.hasOwnProperty.call(euCountriesFile, this.form.supplier_country_code);
		},
		defaultDescription() {
			return descriptionFor(this.form.supplier_name || this.form.pattern);
		},
		isFormValid() {
			const f = this.form;
			return !!f.pattern.trim()
				&& !!f.supplier_name.trim()
				&& !!f.supplier_address.trim()
				&& !!f.supplier_city.trim()
				&& !!f.supplier_country_code
				&& !!f.supplier_vat.trim()
				&& f.vat_rate !== null && f.vat_rate > 0 && f.vat_rate <= 100;
		},
		deletePopupButtons() {
			return [
				{
					text: this.getString("close"),
					negative: true,
					callback: () => this.$refs["delete-popup"].toggleStatus(false),
				},
				{
					text: this.getString("delete"),
					callback: this.confirmDelete,
				},
			];
		},
	},
	methods: {
		fetchRules() {
			this.loading = true;
			API.init().getCardSelfInvoiceRules(this.userStore.currBusinessId, (response) => {
				this.loading = false;
				if(response && response.result === "OK") {
					this.rules = response.rules;
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
		matchTypeName(matchType) {
			return this.getString("self_invoice_match_" + matchType);
		},
		countryName(code) {
			const name = euCountriesFile[code] || nonEuCountriesFile[code];
			return name ? code + " - " + name : code;
		},
		addMerchant() {
			this.pickerOpen = true;
		},
		closePicker(isOpen) {
			if(!isOpen) {
				this.pickerOpen = false;
			}
		},
		openForm(rule) {
			this.form = emptyForm();
			this.editingRuleId = null;
			this.backfill = false;
			this.preview = {count: 0, total_amount: 0};
			if(rule) {
				this.editingRuleId = rule.rule_id;
				Object.keys(this.form).forEach(key => {
					this.form[key] = key === "vat_rate" ? Number(rule.vat_rate) : (rule[key] ?? "");
				});
			}
			this.formOpen = true;
		},
		closeForm(isOpen) {
			if(!isOpen) {
				this.formOpen = false;
			}
		},
		setAddress({data}) {
			this.form.supplier_address = data.via || "";
			this.form.supplier_civic = data.civico || "";
			this.form.supplier_zip = data.zip || "";
			this.form.supplier_city = data.comune || data.provincia || "";
			if(data.country && data.country !== this.form.supplier_country_code) {
				this.selectCountry(data.country);
			}
		},
		openFormFromMerchant(merchant) {
			this.openForm(null);
			this.applyPattern(merchant.merchant_name);
			if(merchant.merchant_country && merchant.merchant_country !== "IT") {
				this.selectCountry(merchant.merchant_country);
			}
			this.loadPreview();
		},
		setPattern(pattern) {
			this.applyPattern(pattern);
			this.schedulePreview();
		},
		applyPattern(pattern) {
			const f = this.form;
			if(!f.supplier_name.trim() || f.supplier_name === f.pattern) {
				f.supplier_name = pattern;
			}
			if(!f.description.trim() || f.description === descriptionFor(f.pattern)) {
				f.description = descriptionFor(pattern);
			}
			f.pattern = pattern;
		},
		setMatchType(matchType) {
			this.form.match_type = matchType;
			this.schedulePreview();
		},
		schedulePreview() {
			if(this.editingRuleId) {
				return;
			}
			clearTimeout(this.previewTimeout);
			this.previewTimeout = setTimeout(this.loadPreview, 400);
		},
		loadPreview() {
			if(!this.form.pattern.trim()) {
				this.preview = {count: 0, total_amount: 0};
				return;
			}
			const pattern = this.form.pattern;
			const matchType = this.form.match_type;
			API.init().previewCardSelfInvoiceRule(this.userStore.currBusinessId, pattern, matchType, (response) => {
				// Ignore answers to a pattern the user has changed since
				if(response && response.result === "OK" && pattern === this.form.pattern && matchType === this.form.match_type) {
					this.preview = {count: response.count, total_amount: response.total_amount};
				}
			});
		},
		selectCountry(code) {
			this.countriesOpen = false;
			if(code === "IT" || code === "OO") {
				this.showNotification("self-invoice-invalid-country");
				return;
			}
			this.form.supplier_country_code = code;
			if(this.isEuCountry) {
				if(this.form.supplier_vat === EXTRA_EU_VAT) {
					this.form.supplier_vat = "";
				}
			} else if(!this.form.supplier_vat.trim()) {
				this.form.supplier_vat = EXTRA_EU_VAT;
			}
		},
		saveRule() {
			if(!this.isFormValid || this.saving) {
				return;
			}
			this.saving = true;
			const rule = {...this.form, rule_id: this.editingRuleId || ""};
			API.init().saveCardSelfInvoiceRule(this.userStore.currBusinessId, rule, !this.editingRuleId && this.backfill, (response) => {
				this.saving = false;
				if(response && response.result === "OK") {
					if(response.backfilled_count > 0) {
						this.showNotification("self-invoice-backfill", [String(response.backfilled_count)]);
					} else {
						this.showNotification("generic-success");
					}
					this.formOpen = false;
					this.pickerOpen = false;
					this.fetchRules();
					this.$emit("saved");
				} else if(response && INVALID_RULE_ERRORS.includes(response.details)) {
					this.showNotification("self-invoice-invalid-rule");
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
		askDelete(rule) {
			this.ruleToDelete = rule;
			this.$refs["delete-popup"].toggleStatus(true);
		},
		confirmDelete() {
			this.$refs["delete-popup"].toggleStatus(false);
			const rule = this.ruleToDelete;
			if(!rule) {
				return;
			}
			this.deletingRuleId = rule.rule_id;
			API.init().deleteCardSelfInvoiceRule(this.userStore.currBusinessId, rule.rule_id, (response) => {
				this.deletingRuleId = null;
				if(response && response.result === "OK") {
					this.showNotification("generic-success");
					this.fetchRules();
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
	},
	watch: {
		active(isActive) {
			if(isActive) {
				this.fetchRules();
			} else {
				this.pickerOpen = false;
				this.formOpen = false;
			}
		},
	},
	beforeUnmount() {
		clearTimeout(this.previewTimeout);
	},
}
</script>

<style scoped lang="scss">
.self-invoice-empty, .form-hint {
	color: var(--color-txt-2);
	margin-bottom: 15px;
}

.form-hint {
	margin: 5px 0 0;
	font-size: 0.85em;
}

.self-invoice-section-title {
	font-weight: 600;
	margin: 20px 0 10px;
}

.rule-row {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	padding: 10px 0;

	.rule-texts {
		min-width: 0;
	}

	.rule-title {
		font-weight: 600;
		word-break: break-word;
	}

	.rule-match, .rule-subtitle {
		color: var(--color-txt-2);
		font-weight: normal;
	}

	.rule-subtitle {
		font-size: 0.85em;
		word-break: break-word;
	}

	.rule-actions {
		margin-left: auto;
		display: flex;
		gap: 8px;
		flex-shrink: 0;
	}
}

.self-invoice-form {
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding-bottom: 20px;

	.self-invoice-section-title {
		margin-bottom: 0;

		&:not(:first-child) {
			margin-top: 15px;
			padding-top: 20px;
			border-top: 1px solid var(--color-border-2);
		}
	}

	.form-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 10px;
	}

	.country-input :deep(input) {
		cursor: pointer;
	}

	.backfill-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 10px;
		gap: 10px;
		padding: 12px;
		border-radius: 10px;
		background-color: var(--color-alpha-2);
	}

	&.wide {
		.form-grid {
			grid-template-columns: 1fr 1fr;
			column-gap: 15px;
			align-items: start;
		}

		.merchant-grid {
			grid-template-columns: 2fr 1fr;
		}

		.invoice-grid {
			grid-template-columns: 1fr 2fr;
		}
	}
}
</style>
